<template>
  <v-row no-gutters align-content="start">
    <v-col 
      cols="12" 
      md="5" 
      class="thickness-wrapper"
      @wheel.prevent="wheel">
      <thickness-map
        v-if="thicknessMap"
        :mapId="name + '-thickness-map-wrapper'"
        :paletteId="name + '-thickness-map-palette-wrapper'"
        :img="thicknessMapImg"
        :size="thicknessMapSize"
        :palette="thicknessMapPalette"
        :paletteSize="thicknessPaletteSize"
        :isLoading="thicknessMapImgLoading || octDataLoading"
        :imgNotFound="thicknessMapImgNotFound"
        :colorSettings="thicknessMapColorSettings"
        :minThickness="minThickness"
        :maxThickness="maxThickness"
        :thicknessStep="thicknessStep"
        :showETDRS="showETDRS"
        :foveaOffset="foveaOffset"
        :thicknessAnalysis="thicknessAnalysis"
        :isMultiview="true"/>
    </v-col>
    <v-col cols="12" md="7" class="reference-wrapper d-flex">
      <oct-reference-image
        :playbackTop="true"
        :referenceId="name + '-reference-image-wrapper'"
        :paletteId="name + '-reference-palette-wrapper'"
        :isLoading="refImgLoading || octDataLoading"
        :refImgNotFound="refImgNotFound"
        :refImg="refImg"
        :refFit="refFit"
        :refSize="refSize"
        :refImgLocation="refImgLocation"
        :slices="slices"
        :sliceCurrent="sliceCurrent"
        :sliceCurrentIndex="sliceCurrentIndex"
        :sliceBounds="sliceBounds"
        :isPlaying="isPlaying"
        :playRate="playRate"
        :drawAllSlices="!showOverlay"
        :showExtension="!showOverlay"
        :showBindingBox="showOverlay"
        :showOverlay="showOverlay"
        :overlayOpacity="overlayOpacity"
        :overlayImg="overlayImg"
        :overlaySize="overlaySize"
        :overlayAnalysis="overlayAnalysis"
        :overlayFoveaOffset="overlayFoveaOffset"
        :showOverlayETDRS="showETDRS"
        :palette="overlayPalette"
        :paletteSize="overlayPaletteSize"
        :paletteMin="overlayMin"
        :paletteMax="overlayMax"
        :paletteStep="overlayStep"
        :isMultiview="true"
        @previous="previousSlice"
        @next="nextSlice"
        @play="play"
        @pause="pause"
        @setPlayRate="setPlayRate"
        @selectSlice="selectSlice"/>
    </v-col>
    <v-col 
      cols="12" 
      class="cirrus-slice-view"
      @wheel.prevent="wheel">
      <oct-slice-view
        :slice-id="name + '-slice-image-wrapper'"
        :fitSize="sliceFit"
        :imgSize="sliceFit"
        :img="sliceImg"
        :imgNotFound="sliceImgNotFound"
        :imgLoading="sliceImgLoading"
        :dataLoading="octDataLoading"
        :sliceEyeMarker="sliceCurrentEyeMarker"
        :strechSegmentation="true"
        :sliceInfo="sliceInfo"
        :showSegmentation="showSegmentation"
        :layerData="sliceSegmentationLayerData"
        :octColorSettings="colorSettings"/>
    </v-col>
  </v-row>
</template>

<script>
import octSliceView from "./OCTSliceView";
import octReferenceImage from "./OCTReferenceImage";
import thicknessMap from "./ThicknessMap";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import cirrusComparisonViewHelper from "../../mixins/CirrusComparisonViewHelper";
import { mapWatchers } from "../../../helpers/utils";
import {
  defaultSize,
  getViewSize,
  setSize,
  setFittedSize,
  defaultTransform,
  setCirrusRefereceFit,
  setTransformSizes
} from "../../../canvas/geometry";
export default {
  name: "CirrusComparisonViewBodyContent",
  components: {
    octSliceView,
    octReferenceImage,
    thicknessMap
  },
  mixins: [responsiveCanvases, cirrusComparisonViewHelper],
  props: {
    name: String,
    context: Object,
    exams: Array,
    groups: Array
  },
  data() {
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      thicknessMapSize: defaultSize(),
      thicknessPaletteSize: defaultSize(),
      overlaySize: defaultSize(),
      overlayPaletteSize: defaultSize(),
      sliceFit: defaultSize(),
      transform: defaultTransform()
    };
  },
  computed: {
    isMacularCube() {
      return this.context.seriesType == "macula";
    },
    scanDisplayRatio() {
      if (this.$store.getters.cirrusScanDisplayRatio && this.$store.getters.cirrusScanDisplayRatio != "default")
        return this.$store.getters.cirrusScanDisplayRatio;
      else
        return this.$store.getters.siteCirrusScanDisplayRatio;
    }
  },
  methods: {
    updateCanvasSizes() {
      let refId = this.name + "-reference-image-wrapper";
      let rvs = getViewSize(refId);
      setSize(this.refSize, rvs);
      setCirrusRefereceFit(this.refSize, this.sliceBounds, this.refFit);

      let sliceId = this.name + "-slice-image-wrapper";
      let svs = getViewSize(sliceId);
      if (!this.isMacularCube || this.scanDisplayRatio == "stretch") 
        setFittedSize(svs, svs, this.sliceFit, 15);
      else {
        let aspRatio = this.sliceInfo.dimensions.pixelWidth > this.sliceInfo.dimensions.pixelHeight ?
          (this.sliceInfo.dimensions.pixelHeight / this.sliceInfo.dimensions.pixelWidth) :
          (this.sliceInfo.dimensions.pixelWidth / this.sliceInfo.dimensions.pixelHeight);
        if (svs.w * aspRatio < svs.h) 
          setSize(this.sliceFit, {w: svs.w, h: svs.h * aspRatio});
        else 
          setSize(this.sliceFit, { w: svs.h / aspRatio, h: svs.h });
      }

      let mapId = this.name + "-thickness-map-wrapper";
      let tms = getViewSize(mapId);
      // ensure that thickness map has the same aspect ratio as the reference image map
      let size = {
        w: this.refFit.w * this.relativeMapSize.w,
        h: this.refFit.h * this.relativeMapSize.h
      };
      setFittedSize(tms, size, this.thicknessMapSize, 5);
      setSize(this.overlaySize, size);
      let paletteId = this.name + "-thickness-map-palette-wrapper";
      let tmps = getViewSize(paletteId);
      setFittedSize(tmps, tmps, this.thicknessPaletteSize, 5);

      let overlayPaletteId = this.name + "-reference-palette-wrapper";
      let ops = getViewSize(overlayPaletteId);
      setFittedSize(ops, ops, this.overlayPaletteSize, 5);

      setTransformSizes(this.transform, this.refFit, this.refFit);
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo", "showOverlay"], function() {
      this.resetView();
      this.updateCanvasSizes();
    })
  },
  mounted() {
    setTimeout(() => this.updateCanvasSizes(), 500);
  }
};
</script>